import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label
} from "@veritrellis/ui";
import { useState } from "react";
import { policyTemplates } from "./templates.js";
import type { PolicyTemplate } from "./templates.js";
import type { BuilderState } from "./builderTypes.js";

type Props = {
  opened: boolean;
  onClose: () => void;
  onSelect: (template: PolicyTemplate) => void;
  onStartBlank: (actionType: string) => void;
};

const modeBadgeVariant = (
  mode: BuilderState["mode"]
): "success" | "destructive" | "warning" | "default" | "secondary" => {
  if (mode === "allow") return "success";
  if (mode === "deny") return "destructive";
  if (mode === "require_approval") return "warning";
  return "default";
};

export const TemplatePickerModal = ({ opened, onClose, onSelect, onStartBlank }: Props) => {
  const [customAction, setCustomAction] = useState("");

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose a starting point</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {policyTemplates.map((t) => (
              <Card
                key={t.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => onSelect(t)}
              >
                <CardContent className="flex flex-col gap-1 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="flex-1 text-sm font-semibold">{t.label}</p>
                    <Badge variant={modeBadgeVariant(t.state.mode)} className="shrink-0">
                      {t.state.mode}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{t.description}</p>
                  <p className="font-mono text-xs text-primary">{t.actionType}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Start from blank</p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[200px] flex-1 flex flex-col gap-1.5">
                <Label>Action type</Label>
                <Input
                  placeholder="e.g. send_invoice"
                  value={customAction}
                  onChange={(e) => setCustomAction(e.currentTarget.value)}
                />
              </div>
              <Button
                variant="outline"
                disabled={customAction.trim().length === 0}
                onClick={() => onStartBlank(customAction.trim())}
              >
                Create blank policy
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
